/**
 * «إعادة تعيين الصفحة» — change which screens an existing user may open.
 *
 * The tree itself now lives in `PagesTree.jsx`, shared with the create/edit
 * user form so the two can no longer drift apart. What is left here is the
 * modal, the save, and the one thing specific to reassigning: the existing
 * selection has to be recovered from `userRoles`, which the users endpoint
 * returns as a comma-joined string of page NAMES rather than ids.
 */

import React, { useEffect, useState } from 'react';
import Modal from '../../../Atoms/Modal/Modal';
import ModalChildrenContainer from '../../../Atoms/Modal/ModalChildrenContainer';
import Buttons from '../../../Atoms/Buttons/Buttons';
import styles from './UserRoleModal.module.css';
import { useAddUser } from '../../../../Hooks/Users';
import { toast } from 'react-toastify';
import LoaderButton from '../../Loader/LoaderButton';
import { useToast } from '../../Custom/CustomToaster/Toast';
import PagesTree, { selectionFromUserRoles } from './PagesTree';

const UserRoleModal = ({
  handleOnClose,
  refetchUsers,
  eachUser = null,
  pagesData,
}) => {
  const { mutate: addUser, isLoading: isAddingUser } = useAddUser();
  const customizedToast = useToast();

  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    if (!eachUser || !pagesData?.length) return;
    setSelected(selectionFromUserRoles(eachUser.userRoles, pagesData));
  }, [eachUser, pagesData]);

  /**
   * Saving NOTHING is allowed, deliberately.
   *
   * This form used to require at least one page (`Yup ... required`), which
   * made revoking an account's last page impossible — the only way to stop
   * someone reaching the CMS was to delete them. An empty grant is now a valid
   * state, and that user meets the no-permission screen at their next sign-in.
   */
  const handleSave = () => {
    addUser(
      {
        userId: eachUser.userId,
        fullNameEn: eachUser.fullName_En,
        fullNameAr: eachUser.fullName_AR,
        email: eachUser.email,
        mobile: eachUser.mobile,
        username: eachUser.userName,
        pages: Array.from(selected),
      },
      {
        onSuccess: () => {
          refetchUsers();
          handleOnClose();
          // Was `values.mode === 'Reassign'`, and `values.mode` never existed,
          // so reassigning pages always reported "user added".
          customizedToast.open('تم إعادة التعيين بنجاح');
        },
        onError: () => {
          toast.error('تعذّر حفظ الصفحات. يرجى المحاولة مرة أخرى.');
        },
      }
    );
  };

  return (
    <Modal
      onClose={handleOnClose}
      title={'إعادة تعيين الصفحة'}
      width='45rem'
      closeWhite={false}
    >
      <ModalChildrenContainer>
        <PagesTree
          pagesData={pagesData}
          selected={selected}
          onChange={setSelected}
        />

        {isAddingUser ? (
          <LoaderButton />
        ) : (
          <div className={styles['button-container']}>
            <Buttons
              text='إلغاء'
              color='#A6A6A6'
              handleOnClick={handleOnClose}
              rounded
              width={200}
              style={{ padding: '15px', fontFamily: 'BukraBold' }}
            />
            <Buttons
              text='تحديث'
              color='rgba(13, 160, 110, 1)'
              handleOnClick={handleSave}
              rounded
              width={200}
              style={{ padding: '15px', fontFamily: 'BukraBold' }}
            />
          </div>
        )}
      </ModalChildrenContainer>
    </Modal>
  );
};

export default UserRoleModal;
